import { formatToDollar } from "@inrev/common";
import type { FC, ReactNode } from "react";
import { formatEmpty } from "../../../../utils";
import {
	EditableMetricCard,
	METRIC_CARD_HOVER_CLASS,
	MetricCurrencyInput,
} from "../../../patterns/metric-card";
import { HISTORY_CARD_LABEL_CLASS } from "./cardStyles";
import {
	type HistoryFieldSubmitCallbacks,
	useEditableHistoryField,
} from "./useEditableHistoryField";

const canSubmitCurrency = (draft: number | null) => draft === null || draft >= 0;

export type HistoryCurrencyCardProps = {
	testId: string;
	label: string;
	value: number | null;
	submit: (draft: number | null, callbacks: HistoryFieldSubmitCallbacks) => void;
	/** Optional long-form question rendered beneath the value. */
	caption?: ReactNode;
	readOnly?: boolean;
	errorHeading?: string;
};

export const HistoryCurrencyCard: FC<HistoryCurrencyCardProps> = ({
	testId,
	label,
	value,
	submit,
	caption,
	readOnly,
	errorHeading,
}) => {
	const { controller, draft, changeValue } = useEditableHistoryField<number | null>({
		value,
		submit,
		canSubmit: canSubmitCurrency,
	});

	return (
		<EditableMetricCard
			testId={testId}
			label={label}
			labelClassName={HISTORY_CARD_LABEL_CLASS}
			className={METRIC_CARD_HOVER_CLASS}
			readOnly={readOnly}
			errorHeading={errorHeading}
			controller={controller}
			confirmTooltipId={`${testId}-tooltip`}
			confirmTooltipContent="Must be a non-negative number"
		>
			<EditableMetricCard.ValueRow data-testid={`${testId}-display`}>
				{controller.isEditing ? (
					<MetricCurrencyInput
						testId={`${testId}-input`}
						value={draft}
						onChange={changeValue}
						disabled={controller.canCancel === false}
					/>
				) : (
					<EditableMetricCard.Value size="sm">
						{formatEmpty(formatToDollar(draft))}
					</EditableMetricCard.Value>
				)}
			</EditableMetricCard.ValueRow>
			{caption && <EditableMetricCard.Caption>{caption}</EditableMetricCard.Caption>}
		</EditableMetricCard>
	);
};
